import * as Yup from "yup";
import i18next from "../../i18n";
import { CompanyBankingInfoRequest, CompanyBankingInfoResponse } from "../../types";

/**
 * One bank-account row managed by `BankingInformationForm`'s `useFieldArray`. Mirrors
 * `CompanyBankingInfoResponse`/`CompanyBankingInfoRequest` (`types/company.type.ts`) field-for-
 * field, MINUS `bankIdentifier`: the FE type declares it, but the backend's own
 * `CompanyProfileUpdateRequest.BankingInfoRequest` (see `spectum`'s payload/request package) has
 * no such field at all — `CompanyService#updateCompanyBankingInfos` never reads it, and
 * `CompanyProfileResponse.java`'s own docstring documents it explicitly as "a banking
 * `bankIdentifier` that the FE type declares but the JPA model never carried". The pre-Carbon
 * form required it in its own client-side gate, but that requirement never protected anything —
 * the value was silently dropped on every save. Dropped here rather than carried forward, per
 * this repo's "delete what is not used" standard, and matching this task's brief (which lists the
 * row's real fields as bankName/accountNumber/accountType/SWIFT/IBAN/currency — no identifier).
 *
 * `routingNumber`/`countryId` were the opposite defect: the backend's `BankingInfoRequest` DOES
 * carry both (persisted by `CompanyService#updateCompanyBankingInfos`, returned by
 * `CompanyProfileResponse.BankingInfoRef`), but the pre-fix FE type/form omitted them entirely.
 * Because `updateCompanyBankingInfos` CLEARS and rebuilds the banking list from every save's
 * payload, a save made through the old form silently NULLED out any `routingNumber`/`countryId`
 * a row already had — real data loss on ordinary use, not just a missing display field. Both are
 * added here as genuinely optional (no backend `@NotNull`/gate requires either).
 */
export interface BankingInfoFormFields {
  bankName: string;
  accountNumber: string;
  accountType: string;
  currency: string;
  swift?: string;
  iban?: string;
  routingNumber?: string;
  countryId?: string;
}

export interface BankingInformationFormFields {
  bankingInfos: BankingInfoFormFields[];
}

const maxLengthMessage = (max: number) =>
  i18next.t("validations.maxLength", {
    max,
    defaultValue: `Must not exceed ${max} characters`,
  });

/**
 * `bankName`/`accountNumber`/`accountType`/`currency` are `.required()` — reproducing the
 * pre-Carbon form's own "Add Bank" gate EXACTLY (`validateBankingInformation`, that form's own
 * `onClick` handler): `bankIdentifier && bankName && accountNumber && currency && accountType`
 * all had to be truthy before a row could ever be staged (minus `bankIdentifier`, see this file's
 * top docstring). That gate is also why requiring these four for every row — not just new ones —
 * is safe: the only path that ever created a `CompanyBankingInfo` row was through this exact
 * gate, so every already-persisted row already satisfies it. `swift`/`iban` were always optional
 * (not part of the gate) and stay that way.
 *
 * Every max-length mirrors `CompanyProfileUpdateRequest.BankingInfoRequest`'s own `@Size`
 * constraints exactly: `bankName` 255, `accountNumber`/`accountType` 100, `swift`/`iban`/
 * `currency`/`routingNumber` 50. `countryId` carries no `@Size` on the backend (it's a `UUID`,
 * same as `AddressRequest.countryId`) — no format check here, same as `addressRowValidationSchema`
 * skips one for a genuinely-optional field.
 */
export const bankingInfoRowValidationSchema = Yup.object({
  bankName: Yup.string()
    .required(i18next.t("validations.bankName"))
    .max(255, maxLengthMessage(255)),
  accountNumber: Yup.string()
    .required(i18next.t("validations.accountNumber"))
    .max(100, maxLengthMessage(100)),
  accountType: Yup.string()
    .required(i18next.t("validations.accountType"))
    .max(100, maxLengthMessage(100)),
  currency: Yup.string()
    .required(i18next.t("validations.selectCurrency"))
    .max(50, maxLengthMessage(50)),
  swift: Yup.string().max(50, maxLengthMessage(50)),
  iban: Yup.string().max(50, maxLengthMessage(50)),
  routingNumber: Yup.string().max(50, maxLengthMessage(50)),
  countryId: Yup.string().optional(),
});

/**
 * The pre-Carbon form never validated the LIST itself (zero bank accounts was, and stays, a
 * valid state) — only each staged row, at the moment it was added. No `.min()`/`.required()` on
 * the array, same rationale as `addressesValidationSchema.ts`.
 */
export const bankingInformationValidationSchema = Yup.object({
  bankingInfos: Yup.array().of(bankingInfoRowValidationSchema).default([]),
});

/**
 * Maps one validated form row onto the `PUT /company/{id}/profile` payload shape
 * (`CompanyBankingInfoRequest`). Pulled out of `BankingInformationForm`'s `onSubmit` so the
 * mapping itself is unit-testable without rendering the form — this is the exact spot the
 * `routingNumber`/`countryId` data-loss bug lived: the map used to list only
 * bankName/accountNumber/accountType/currency/swift/iban/bankIdentifier, silently dropping
 * whatever the loaded row already had in the two new fields before sending the CLEAR-AND-REBUILD
 * payload (`CompanyService#updateCompanyBankingInfos`) back to the backend. `bankIdentifier` is
 * still sent as `""` — see this file's top docstring — a phantom field the backend never reads.
 */
export const buildBankingInfoRequest = (
  bankingInfo: BankingInfoFormFields
): CompanyBankingInfoRequest => ({
  ...bankingInfo,
  bankIdentifier: "",
});

/**
 * The other half of the round-trip: seeds one `useFieldArray` row from a `GET`'d
 * `CompanyBankingInfoResponse`. Pulled out of `BankingInformationForm`'s `defaultValues` for the
 * same testability reason as `buildBankingInfoRequest` above — this is actually where the
 * data-loss bug originated: the pre-fix seeding mapped bankName/accountNumber/accountType/
 * currency/swift/iban field-by-field and silently left `routingNumber`/`countryId` out, so even a
 * row that HAD both values already lost them from the form the moment it loaded — before the user
 * changed anything, a plain "load then Save" wiped them via the backend's clear-and-rebuild.
 */
export const buildBankingInfoFormRow = (
  bankingInfo: CompanyBankingInfoResponse
): BankingInfoFormFields => ({
  bankName: bankingInfo.bankName ?? "",
  accountNumber: bankingInfo.accountNumber ?? "",
  accountType: bankingInfo.accountType ?? "",
  currency: bankingInfo.currency ?? "",
  swift: bankingInfo.swift ?? "",
  iban: bankingInfo.iban ?? "",
  routingNumber: bankingInfo.routingNumber ?? "",
  countryId: bankingInfo.countryId ?? "",
});
